import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { RS_BREADCRUMBS_LOCAL_STORAGE_KEY } from '@Pages/RetroSynthesis/RsBreadcrumbs/constants';
import isEmpty from 'lodash/isEmpty';
import forEach from 'lodash/forEach';
import difference from 'lodash/difference';
import isEqual from 'lodash/isEqual';
import sortBy from 'lodash/sortBy';
import filter from 'lodash/filter';
import { getUrlParamsArrayByName, getValueFromStorage, putValueToStorage, removeValueFromStorage } from '@Pages/RetroSynthesis/utils';
import { PageName, RsRoute } from '@Pages/RetroSynthesis/routes';
import { Breadcrumb } from '@els/els-react--breadcrumb';
import { RouteType, UrlParams } from '@Pages/RetroSynthesis/constants';
import {
  DrawTooltipContent,
  ProjectListTooltipContent,
  ProjectRouteTooltipContent,
  ProjectTooltipContent,
} from '@Pages/RetroSynthesis/RsBreadcrumbs/RsBreadcrumbsTooltipContent';
import { ProjectRoutesCountMapState } from '@Pages/RetroSynthesis/RsBreadcrumbs/reducer';

import './RsBreadcrumbs.scss';
import {Link} from "react-router-dom";

/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react-refresh/only-export-components */
const getData = () => getValueFromStorage(RS_BREADCRUMBS_LOCAL_STORAGE_KEY, true) || [];

const putData = (value) => putValueToStorage(RS_BREADCRUMBS_LOCAL_STORAGE_KEY, value, true);

export const clearRsBreadcrumbs = () => removeValueFromStorage(RS_BREADCRUMBS_LOCAL_STORAGE_KEY, true);

const getPartAt = (index, parts = []) => (parts.length > index ? parts[index] : undefined);

const getRouteBreadcrumbLabel = (routeType, routeId) => {
  switch (routeType) {
    case RouteType.PUBLISHED:
      return `Published Route #${routeId}`;
    case RouteType.PREDICTED:
      return `Predicted Route #${routeId}`;
    case RouteType.CUSTOMIZED:
      return `Custom Route #${routeId}`;
    case RouteType.COMBINED:
      return `Combined Route #${routeId}`;
    default:
      return `Route #${routeId}`;
  }
};

const isSubsetArray = (subset, superset) => difference(subset, superset)?.length === 0;

const isEqualArray = (a, b) => isEqual(sortBy(a), sortBy(b));

const defineNewLimits = (newLimit, limits) => {
  if (isEmpty(newLimit)) {
    return;
  }
  const foundInArray = !isEmpty(filter(limits, (a) => isEqualArray(newLimit, a)));
  if (foundInArray) {
    // eslint-disable-next-line consistent-return
    return limits;
  }
  const result = [];
  forEach(limits, (lim) => {
    if (!isEqualArray(newLimit, lim) && isSubsetArray(newLimit, lim)) {
      result.push(lim);
    }
  });
  result.push(newLimit);
  // eslint-disable-next-line consistent-return
  return result;
};

const parseLocation = (location) => {
  const result = {};
  const parts = (location?.pathname && location.pathname?.split('/').filter((p) => !isEmpty(p))) || [];

  result.isRS = getPartAt(0, parts) === PageName.RETRO_SYNTHESIS;
  result.isProjects = getPartAt(1, parts) === PageName.MY_PROJECTS;
  result.projectId = getPartAt(2, parts)?.split('?')[0];
  result.limit = getUrlParamsArrayByName(location, UrlParams.LIMIT_TO);
  result.routeId = getPartAt(3, parts);
  result.routeView = getPartAt(4, parts);
  return result;
};

// Helpers used to split complex logic in handleUrlChanges
const computeLimitsForNewData = (oldData, newData, removedRouteIds) => {
  // return an object with possibly updated limit and limits without mutating inputs
  if (!(oldData.isProjects && newData.isProjects && !newData.routeId)) {
    return newData.limit ? { limits: [newData.limit] } : {};
  }

  const newLimit = newData.limit;
  const newLimitIsArray = Array.isArray(newLimit);
  const newLimitLength = newLimitIsArray ? newLimit.length : 0;
  const hasRemovedAndLimit = removedRouteIds && removedRouteIds.length && newLimitIsArray && newLimit.length;
  if (hasRemovedAndLimit) {
    if (removedRouteIds.length === newLimitLength) {
      return { limit: [], limits: [] };
    }
    return { limits: [newLimit] };
  }

  return { limits: defineNewLimits(newLimit, oldData.limits) };
};

const sanitizeResultAfterMerge = (result, oldData, newData) => {
  // return a new object rather than mutating the passed-in result
  const out = { ...result };
  if (oldData.projectId && newData.projectId && oldData.projectId !== newData.projectId && !newData.routeId) {
    out.limit = undefined;
    out.limits = undefined;
    out.routeId = undefined;
    return out;
  }
  if (oldData.routeId !== out.routeId && out.limits) {
    out.limits = out.limits.filter((limit) => isSubsetArray([newData.routeId], limit));
  }
  if (!newData.routeId && newData.limits && !isSubsetArray([oldData.routeId], newData.limit)) {
    out.routeId = undefined;
  }
  return out;
};

const applyRemovedEntitiesCleanup = (result, removedProjectIds, removedRouteIds) => {
  // return a new object rather than mutating the passed-in result
  const out = { ...result };
  if (removedProjectIds && !isEmpty(removedProjectIds) && removedProjectIds.some((id) => String(id) === out.projectId)) {
    if (removedRouteIds && !isEmpty(removedRouteIds) && removedRouteIds.some((id) => String(id) === out.routeId)) {
      // remove route from breadcrumbs
      out.limit = undefined;
      out.limits = undefined;
      out.routeId = undefined;
      out.routeView = undefined;
    } else if (isEmpty(removedRouteIds)) {
      // remove project from breadcrumbs
      out.limit = undefined;
      out.limits = undefined;
      out.routeId = undefined;
      out.routeView = undefined;
      out.projectId = undefined;
    }
  }
  return out;
};

const handleUrlChanges = (location, { removedProjectIds, removedRouteIds }) => {
  const oldData = getData();
  if (!location) {
    return oldData;
  }
  const newData = parseLocation(location);

  if (newData && !newData.isRS) {
    return {};
  }

  // compute new limit/limits without mutating newData
  const computed = computeLimitsForNewData(oldData, newData, removedRouteIds);
  const newDataClean = { ...newData, ...computed };

  Object.keys(newDataClean)?.forEach((key) => newDataClean[key] === undefined && delete newDataClean[key]);
  let result = { ...oldData, ...newDataClean, limit: newDataClean.limit };

  result = sanitizeResultAfterMerge(result, oldData, newDataClean);
  result = applyRemovedEntitiesCleanup(result, removedProjectIds, removedRouteIds);

  putData(result);
  return result;
};

const FlyoutPlacements = {
  BOTTOM_START: 'bottom-start',
};

// Helper to create common flyoutProps used throughout breadcrumbs
const makeFlyoutProps = (flyout) => ({
  flyout,
  trigger: 'hover',
  pointer: true,
  pointerType: 'primary',
  placement: FlyoutPlacements.BOTTOM_START,
});

// Helpers to create breadcrumb items (link or current)
const makeLinkItem = (children, href, flyout) => ({
  type: 'link',
  linkElementProps: { children, href, routerComponent: Link },
  flyoutProps: makeFlyoutProps(flyout),
});

const makeCurrentItem = (label, flyout) => ({
  type: 'current',
  label,
  flyoutProps: makeFlyoutProps(flyout),
});

function RsBreadcrumbs({
  projectsInfoMap = {},
  location,
  history,
  fetchProjectInfo = () => null,
  routeType,
  removedProjectIds,
  removedRouteIds,
}) {
  const [data, setData] = useState(handleUrlChanges(location, { routeType, removedProjectIds, removedRouteIds }));

  useEffect(
    () => history.listen((newLocation) => setData(handleUrlChanges(newLocation, { routeType, removedProjectIds, removedRouteIds }))),
    [history]
  );

  useEffect(() => {
    setData(handleUrlChanges(location, { routeType, removedProjectIds, removedRouteIds }));
  }, [removedProjectIds, removedRouteIds]);

  // Small helpers to keep getBreadcrumbItems simple and readable
  const createDrawCrumb = () => [makeLinkItem('Draw', RsRoute.DRAW_STRUCTURE, <DrawTooltipContent />)];

  const createProjectsCrumb = (loc) => {
    if (loc.isProjects && isEmpty(loc.projectId)) {
      return [makeCurrentItem('My Synthesis Projects', <ProjectListTooltipContent />)];
    }
    return [makeLinkItem('My Synthesis Projects', RsRoute.PROJECTS_LIST, <ProjectListTooltipContent />)];
  };

  const pushProjectNameCrumb = (crumbs, projectId, projectInfo, loc) => {
    const projectName = projectInfo?.projectName ? projectInfo.projectName : '';
    if (!isEmpty(loc.projectId) && isEmpty(loc.limit) && isEmpty(loc.routeId)) {
      crumbs.push(makeCurrentItem(projectName, <ProjectTooltipContent routesInfo={projectInfo} />));
    } else {
      crumbs.push(makeLinkItem(projectName, RsRoute.getProjectLink(projectId), <ProjectTooltipContent routesInfo={projectInfo} />));
    }
  };

  const pushRouteCrumb = (crumbs, projectId, projectInfo, loc, routeId, routeView) => {
    const routeLabel = getRouteBreadcrumbLabel(routeType, routeId);
    if (!isEmpty(loc.routeId)) {
      crumbs.push(makeCurrentItem(routeLabel, <ProjectRouteTooltipContent routeLabel={routeLabel} projectInfo={projectInfo} />));
    } else {
      crumbs.push(
        makeLinkItem(
          routeLabel,
          RsRoute.getProjectRouteLink(projectId, routeId, routeView),
          <ProjectRouteTooltipContent routeLabel={routeLabel} projectInfo={projectInfo} />
        )
      );
    }
  };

  const createProjectDetailsCrumbs = (projectId, projectInfo, loc, limit, limits, routeId, routeView) => {
    const crumbs = [];
    if (!projectId) return crumbs;

    if (!projectInfo) {
      fetchProjectInfo(projectId);
    }

    if (projectInfo !== ProjectRoutesCountMapState.ERROR) {
      pushProjectNameCrumb(crumbs, projectId, projectInfo, loc);
    }

    if (!isEmpty(limits)) {
      forEach(limits, (lm) => {
        const label = `${lm.length}`;
        const flyout = `Project No #${projectId} limited by routes: ${lm}`;
        if (isEqualArray(lm, limit)) {
          crumbs.push(makeCurrentItem(label, flyout));
        } else {
          crumbs.push(makeLinkItem(label, RsRoute.getProjectLinkLimitTo(projectId, lm), flyout));
        }
      });
    }

    if (!isEmpty(routeId) && projectInfo !== ProjectRoutesCountMapState.ERROR) {
      pushRouteCrumb(crumbs, projectId, projectInfo, loc, routeId, routeView);
    }

    return crumbs;
  };

  const getBreadcrumbItems = () => {
    const { isRS, projectId, limit, limits, routeId, routeView } = data;
    const loc = parseLocation(location);

    if (!isRS) return [];

    return [
      ...createDrawCrumb(),
      ...createProjectsCrumb(loc),
      ...createProjectDetailsCrumbs(projectId, projectsInfoMap[projectId], loc, limit, limits, routeId, routeView),
    ];
  };

  return (
    <Breadcrumb
      className="u-els-padding-top u-els-padding-bottom u-els-padding-left-2x u-els-padding-right-2x u-els-background-color-n0 u-els-font-size-base rs-breadcrumbs"
      itemData={getBreadcrumbItems()}
    />
  );
}

RsBreadcrumbs.propTypes = {
  projectsInfoMap: PropTypes.object,
  history: PropTypes.object,
  location: PropTypes.object,
  fetchProjectInfo: PropTypes.func,
  routeType: PropTypes.string,
  removedProjectIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  removedRouteIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
};

export default RsBreadcrumbs;
